/* eslint-disable @typescript-eslint/naming-convention */
import * as React from 'react';
import { useSelector } from 'react-redux';
import { InwinkAgent } from '@inwink/aiinputs/inwinkagent/inwinkagent';
import { SSETransportProvider } from '@inwink/aiinputs/inwinkagent/inwinkagent.transport.sse';
import type {
    IAgentRenderers, IAgentTool, IToolCallRendererProps, IToolResultRendererProps
} from '@inwink/aiinputs/inwinkagent/inwinkagent.types';
import { combine } from '@inwink/utils/url';
import { getBackOfficeProxy } from '@@modules/admin/api';
import { States } from '@@services/services';
import { InwinkSpinner } from '@@components/ui/inwinkspinner';

import "./aiassistantagent.less";
import { AppTextLabel } from '@@services/i18nservice';
import { I18NResourcesContext } from '@inwink/i18n';
import { quickRepliesTool } from './tools.suggestions';

// ============================================
// Configuration
// ============================================

const AGENT_ENDPOINT = "ai-agent";

// ============================================
// Tools de test
// ============================================

const agentTools: IAgentTool[] = [
    quickRepliesTool,
    {
        name: "contact_inwink_support",
        description: "This tool shows a 'contact Inwink support' action to the user,"
            + " providing details of the issue faced by the user."
            + "Use this tool when the user is facing an issue that requires intervention from Inwink support team, "
            + "but ONLY after exhausting other options such as searching knowledge base."
            + "You must NOT call this tool without gathering detailed information about the user's problem "
            + "and ideally the steps to reproduce it, "
            + "as this will create unnecessary support tickets and burden the support team.\n\n"
            + "If suggesting user to contact support, do not mention the button, stay conversational."
            + "ex: 'Do you want to contact inwink support ?'\n\n"
            + "AFTER calling this tool, the user will be presented with a button to contact Inwink support, "
            + "do not assume any subsequent action and do not provide any additional instruction to the user.\n"
            // eslint-disable-next-line max-len
            + "NEVER TELLS USER YOU CONTACTED SUPPORT, just provide the button and let the user decide whether to click it or not.",
        parameters: {
            type: "object",
            properties: {
                problemTitle: {
                    type: "string",
                    // eslint-disable-next-line max-len
                    description: "Title of the problem faced by the user (will be used as the title of the support ticket created)",
                },
                problemDescription: {
                    type: "string",
                    description: "Detailed description of the problem faced by the user",
                },
                stepsToReproduce: {
                    type: "string",
                    description: "Steps to reproduce the issue, if applicable",
                },
            },
        },
        onExecute: (args) => {
            return JSON.stringify(args);
        },
        toolResultRenderer: ContactInwinkSupport
    },
];

// ============================================
// Props
// ============================================

export interface IInwinkBackofficeAgentProps {
    children?: React.ReactNode;
}

const globalRenderers: IAgentRenderers = {
    runningIndicator: () => <div className="running-indicator">
        <InwinkSpinner animation="spin" />
    </div>,
    step: (p) => {
        if (p.status !== "running") {
            return null;
        }
        let content = <AppTextLabel
            i18n={"eventagent.step." + p.stepName?.toLowerCase()}
            defaultText={p.stepName}
        />;
        if (p.stepName === "Thinking...") {
            content = <AppTextLabel
                i18n={"inwinkagent.chat.thinking." + (Math.floor(Math.random() * 10) + 1)}
                defaultText={p.stepName}
            />;
        }

        return <div className="agent-step">
            {content}
        </div>;
    },
    toolCall: {
        SearchKnowledgeBase: (p: IToolCallRendererProps) => {
            if (p.status !== "completed") {
                return <div className="toolcall search-knowledge-base">
                    Searching online help...
                </div>;
            }
            return null;
        },
    },
    toolResult: {
        ShowHelpPageLinksToUser: HelpPagesLinks
    }
};
// ============================================
// Component
// ============================================

export const InwinkBackofficeAgent = (props: IInwinkBackofficeAgentProps) => {
    const user = useSelector((state: States.IAppState) => state.user);
    const transport = React.useMemo(() => {
        return new SSETransportProvider({
            useHistory: true,
            endpoint: combine(inwink.config.backOfficeApiUrl, AGENT_ENDPOINT),
            headers: () => {
                const proxy = getBackOfficeProxy();
                if (proxy.getApiOptions) {
                    return proxy.getApiOptions('GET').then((options) => {
                        return options?.headers;
                    });
                }
            },
        });
    }, []);

    // Callbacks
    const handleThreadCreated = React.useCallback((newThreadId: string) => {
        console.log("[InwinkBackofficeAgent] Thread created:", newThreadId);
    }, []);

    const handleError = React.useCallback((error: { message: string; code?: string }) => {
        console.error("[InwinkBackofficeAgent] Error:", error.message, error.code);
    }, []);

    return <I18NResourcesContext resources={trads}>
        <InwinkAgent
            provider={transport}
            threadId={user.currentUser?.id}
            tools={agentTools}
            renderers={globalRenderers}
            onThreadCreated={handleThreadCreated}
            onError={handleError}
        >
            {props.children}
        </InwinkAgent>
    </I18NResourcesContext>;
};

function ContactInwinkSupport(props: IToolResultRendererProps) {
    const user = useSelector((state: States.IAppState) => state.user);
    const event = useSelector((state: States.IAppState) => state.event);
    const community = useSelector((state: States.IAppState) => state.community);

    let text = props.args.problemDescription;
    if (text && props.args.stepsToReproduce) {
        text += `\n\n${props.args.stepsToReproduce}`;
    }

    return <div className="toolresult contact-support">
        <AppTextLabel i18n="backofficeagent.tool.contactsupport" component="p" />
        <div className="contact-support-instruction">
            <h4>{props.args.problemTitle}</h4>
            <p>
                {props.args.problemDescription}
            </p>
            <p>
                {props.args.stepsToReproduce}
            </p>
        </div>
        <button onClick={(arg: React.MouseEvent<any>) => {
            arg.preventDefault();
            arg.stopPropagation();

            let url = "https://help.inwink.com/kb-tickets/new?backoffice_url=" + encodeURIComponent(window.location.href);
            if (user.currentUser?.email) {
                url = url + "&email=" + encodeURIComponent(user.currentUser?.email);
            }
            if (user.currentUser?.firstname) {
                url = url + "&firstname=" + encodeURIComponent(user.currentUser?.firstname);
            }
            if (user.currentUser?.lastname) {
                url = url + "&lastname=" + encodeURIComponent(user.currentUser?.lastname);
            }
            if (text) {
                url = url + "&content=" + encodeURIComponent(text);
            }
            if (props.args.problemTitle) {
                url = url + "&subject=" + encodeURIComponent(props.args.problemTitle);
            }

            let p = Promise.resolve(null);
            if (event?.eventdetail) {
                p = import('@@event/data/openpreview').then((mod) => {
                    return mod.getEventPreviewUrl(event?.eventdetail);
                });

            } else if (community?.detail) {
                p = import('@@community/data/openpreview').then((mod) => {
                    return mod.getCommunityPreviewUrl(
                        community?.detail
                    );
                });

            }

            p.then((previewUrl) => {
                if (previewUrl) {
                    url = url + "&preview_or_website_url=" + encodeURIComponent(previewUrl);
                }
                window.open(url, "_blank");
            });
        }}>
            <AppTextLabel i18n="backofficeagent.tool.contactsupport.action" />
        </button>
    </div>;
}

function HelpPagesLinks(p: IToolResultRendererProps) {
    // return <div className="toolresult">
    //     {JSON.stringify(p)}
    // </div>;
    if (!p || !p.result) {
        return null;
    }
    let items: any[] = null;
    try {
        items = p.result ? JSON.parse(p.result) : null;
    } catch (e) {
        console.error("Error parsing tool result:", e);
        return null;
    }
    if (!items || !Array.isArray(items)) {
        return null;
        // return <div className="toolresult">
        //     {JSON.stringify(p)}
        // </div>;
    }

    return <div className="toolresult helppagelinks">{items?.map((link) => {
        return <div key={link.pageUrl} className="helppage-link">
            <a rel="noopener noreferrer" target="_blank" href={link.pageUrl}>
                {link.title}
            </a>
        </div>;
    })}</div>;
}

const trads = {
    "fr": {
        "backofficeagent.tool.contactsupport": "Contacter le support inwink",
        "backofficeagent.tool.contactsupport.action": "Créer un ticket de support",
        "inwinkagent.chat.clear": "Effacer",
        "inwinkagent.chat.clear.confirm.title": "Effacer l'historique de conversation",
        "inwinkagent.chat.clear.confirm.message": "Êtes-vous sûr de vouloir effacer l'historique de conversation ?",        
        "inwinkagent.chat.loading.history": "Chargement de l'historique...",
        "inwinkagent.chat.clearing.history": "Suppression de l'historique...",
        "inwinkagent.chat.input.placeholder": "Écrivez votre message...",
        "inwinkagent.chat.input.send": "Envoyer",
        "inwinkagent.chat.input.cancel": "Annuler",
        "inwinkagent.chat.debug.toolcall.preparing": "En préparation...",
        "inwinkagent.chat.debug.toolcall.executing": "En cours...",
        "inwinkagent.chat.debug.toolcall.completed": "Terminé",
        "inwinkagent.chat.debug.toolcall.error": "Erreur",
        "inwinkagent.chat.debug.toolcall.args": "Arguments",
        "inwinkagent.chat.debug.toolcall.result": "Résultat",
        "inwinkagent.chat.message.feedback": "Cette réponse vous a-t-elle été utile ?",
        "inwinkagent.chat.message.feedback.yes": "Cette réponse m'a été utile",
        "inwinkagent.chat.message.feedback.no": "Cette réponse ne m'a pas été utile",
        "inwinkagent.chat.thinking.1": "Laissez-moi réfléchir...",
        "inwinkagent.chat.thinking.2": "Je suis en train d'analyser les données...",
        "inwinkagent.chat.thinking.3": "Je cherche la meilleure réponse...",
        "inwinkagent.chat.thinking.4": "Un instant, je traite votre demande...",
        "inwinkagent.chat.thinking.5": "Je rassemble les informations dont j'ai besoin...",
        "inwinkagent.chat.thinking.6": "Je travaille dessus...",
        "inwinkagent.chat.thinking.7": "Je cherche la meilleure solution...",
        "inwinkagent.chat.thinking.8": "J'analyse les détails...",
        "inwinkagent.chat.thinking.9": "Je considère les options...",
        "inwinkagent.chat.thinking.10": "J'y suis presque..."
    },
    "en": {
        "backofficeagent.tool.contactsupport": "Contact inwink support",
        "backofficeagent.tool.contactsupport.action": "Create a support ticket",
        "inwinkagent.chat.clear": "Clear",
        "inwinkagent.chat.clear.confirm.title": "Clear conversation history",
        "inwinkagent.chat.clear.confirm.message": "Are you sure you want to clear the conversation history?",        
        "inwinkagent.chat.loading.history": "Loading history...",
        "inwinkagent.chat.clearing.history": "Clearing history...",
        "inwinkagent.chat.input.placeholder": "Type your message...",
        "inwinkagent.chat.input.send": "Send",
        "inwinkagent.chat.input.cancel": "Cancel",
        "inwinkagent.chat.debug.toolcall.preparing": "Preparing...",
        "inwinkagent.chat.debug.toolcall.executing": "Executing...",
        "inwinkagent.chat.debug.toolcall.completed": "Completed",
        "inwinkagent.chat.debug.toolcall.error": "Error",
        "inwinkagent.chat.debug.toolcall.args": "Arguments",
        "inwinkagent.chat.debug.toolcall.result": "Result",
        "inwinkagent.chat.message.feedback": "Was this response helpful?",
        "inwinkagent.chat.message.feedback.yes": "This response was helpful",
        "inwinkagent.chat.message.feedback.no": "This response was not helpful",
        "inwinkagent.chat.thinking.1": "Let me think about it...",
        "inwinkagent.chat.thinking.2": "I'm analyzing the data...",
        "inwinkagent.chat.thinking.3": "I'm looking for the best answer...",
        "inwinkagent.chat.thinking.4": "Just a moment, I'm processing your request...",
        "inwinkagent.chat.thinking.5": "I'm gathering the information I need...",
        "inwinkagent.chat.thinking.6": "I'm working on it...",
        "inwinkagent.chat.thinking.7": "I'm trying to find the best solution...",
        "inwinkagent.chat.thinking.8": "I'm analyzing the details...",
        "inwinkagent.chat.thinking.9": "I'm considering the options...",
        "inwinkagent.chat.thinking.10": "I'm almost there..."
    },
    "es": {
        "backofficeagent.tool.contactsupport": "Contactar con el soporte de inwink",
        "backofficeagent.tool.contactsupport.action": "Crear un ticket de soporte",
        "inwinkagent.chat.clear": "Borrar",
        "inwinkagent.chat.clear.confirm.title": "Borrar historial de conversación",
        "inwinkagent.chat.clear.confirm.message": "¿Estás seguro de que deseas borrar el historial de conversación?",        
        "inwinkagent.chat.loading.history": "Cargando historial...",
        "inwinkagent.chat.clearing.history": "Borrando historial...",
        "inwinkagent.chat.input.placeholder": "Escribe tu mensaje...",
        "inwinkagent.chat.input.send": "Enviar",
        "inwinkagent.chat.input.cancel": "Cancelar",
        "inwinkagent.chat.debug.toolcall.preparing": "Preparando...",
        "inwinkagent.chat.debug.toolcall.executing": "Ejecutando...",
        "inwinkagent.chat.debug.toolcall.completed": "Completado",
        "inwinkagent.chat.debug.toolcall.error": "Error",
        "inwinkagent.chat.debug.toolcall.args": "Argumentos",
        "inwinkagent.chat.debug.toolcall.result": "Resultado",
        "inwinkagent.chat.message.feedback": "¿Fue útil esta respuesta?",
        "inwinkagent.chat.message.feedback.yes": "Esta respuesta fue útil",
        "inwinkagent.chat.message.feedback.no": "Esta respuesta no fue útil",
        "inwinkagent.chat.thinking.1": "Déjame pensar...",
        "inwinkagent.chat.thinking.2": "Estoy analizando los datos...",
        "inwinkagent.chat.thinking.3": "Estoy buscando la mejor respuesta...",
        "inwinkagent.chat.thinking.4": "Un momento, estoy procesando tu solicitud...",
        "inwinkagent.chat.thinking.5": "Estoy reuniendo la información que necesito...",
        "inwinkagent.chat.thinking.6": "Estoy trabajando en ello...",
        "inwinkagent.chat.thinking.7": "Estoy tratando de encontrar la mejor solución...",
        "inwinkagent.chat.thinking.8": "Estoy analizando los detalles...",
        "inwinkagent.chat.thinking.9": "Estoy considerando las opciones...",
        "inwinkagent.chat.thinking.10": "Ya casi termino..."
    }
};